import { jsPDF } from 'jspdf';
import logoUrl from '../assets/edvergenceX.png';

const PAGE_MARGIN = 48;
const BODY_COLOR = [51, 65, 85];      // slate-700
const MUTED_COLOR = [100, 116, 139];  // slate-500
const BRAND_DARK = [15, 23, 42];      // slate-900
const PRIMARY_BLUE = [37, 99, 235];   // blue-600
const SUCCESS_GREEN = [16, 185, 129]; // emerald-500
const WARNING_AMBER = [217, 119, 6];  // amber-600
const BORDER_LIGHT = [226, 232, 240]; // slate-200

const asText = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
};

const loadImageData = async () => {
  try {
    const response = await fetch(logoUrl);
    if (!response.ok) throw new Error(`Logo request failed with status ${response.status}`);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Logo could not be read'));
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Resume Scorer PDF generated without logo:', error);
    return null;
  }
};

export const createResumeScorerPdf = async (scorerResult, studentProfile = null) => {
  if (!scorerResult) {
    throw new Error('No resume scorer evaluation data found.');
  }

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  if (typeof doc.setCharSpace === 'function') doc.setCharSpace(0);
  if (typeof doc.setLineHeightFactor === 'function') doc.setLineHeightFactor(1.2);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PAGE_MARGIN * 2;
  let y = PAGE_MARGIN;

  const logoData = await loadImageData();

  const addFooter = () => {
    doc.setDrawColor(...BORDER_LIGHT);
    doc.setLineWidth(1);
    doc.line(PAGE_MARGIN, pageHeight - 38, pageWidth - PAGE_MARGIN, pageHeight - 38);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED_COLOR);
    doc.text('EdVergenceX | AI Resume Evaluation & Benchmark', PAGE_MARGIN, pageHeight - 22);
    doc.text(`Page ${doc.getNumberOfPages()}`, pageWidth - PAGE_MARGIN, pageHeight - 22, { align: 'right' });
  };

  const newPage = () => {
    addFooter();
    doc.addPage();
    y = PAGE_MARGIN + 10;
  };

  const ensureSpace = (height = 24) => {
    if (y + height > pageHeight - 56) newPage();
  };

  const truncateToWidth = (text, maxWidth) => {
    const s = asText(text, 'N/A');
    if (doc.getTextWidth(s) <= maxWidth) return s;
    let truncated = s;
    while (truncated.length > 3 && doc.getTextWidth(truncated + '...') > maxWidth) {
      truncated = truncated.slice(0, -1);
    }
    return truncated + '...';
  };

  const addWrappedText = (text, options = {}) => {
    const { fontSize = 10, color = BODY_COLOR, lineHeight = 14, indent = 0, gapAfter = 8, fontStyle = 'normal' } = options;
    const lines = doc.splitTextToSize(asText(text, ''), contentWidth - indent);
    doc.setFont('helvetica', fontStyle);
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    lines.forEach((line) => {
      ensureSpace(lineHeight);
      doc.text(line, PAGE_MARGIN + indent, y);
      y += lineHeight;
    });
    y += gapAfter;
  };

  const addSectionHeader = (title, color = PRIMARY_BLUE) => {
    ensureSpace(34);
    doc.setFillColor(...color);
    doc.roundedRect(PAGE_MARGIN, y - 14, 4, 20, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...BRAND_DARK);
    doc.text(title, PAGE_MARGIN + 14, y + 1);
    y += 24;
  };

  // 1. Top Header Banner
  const NAVY = [26, 10, 20];
  const ROSE = [244, 63, 94];
  const WHITE = [255, 255, 255];
  const scoreVal = typeof scorerResult.score === 'number' ? scorerResult.score : parseInt(scorerResult.score, 10) || 0;
  const scoreColor = scoreVal >= 80 ? SUCCESS_GREEN : scoreVal >= 60 ? WARNING_AMBER : [239, 68, 68];

  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 218, 'F');
  doc.setFillColor(...scoreColor);
  doc.rect(0, 213, pageWidth, 8, 'F');
  doc.setFillColor(...ROSE);
  doc.rect(0, 213, pageWidth / 3, 8, 'F');

  if (logoData) {
    try {
      doc.addImage(logoData, 'PNG', PAGE_MARGIN, 14, 120, 38, undefined, 'FAST');
    } catch (_) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...WHITE);
      doc.text('EdVergenceX', PAGE_MARGIN, 38);
    }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...WHITE);
    doc.text('EdVergenceX', PAGE_MARGIN, 38);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...WHITE);
  doc.text('AI RESUME SCORER REPORT', PAGE_MARGIN, 102);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(148, 163, 184);
  doc.text('Resume Evaluation & Benchmark', PAGE_MARGIN, 120);

  // 2. Student & Document Meta
  const studentName = studentProfile?.full_name || studentProfile?.name || 'Student';
  const institution = studentProfile?.institution_name || studentProfile?.university_name || 'N/A';
  const usn = studentProfile?.usn || 'N/A';
  
  const createdDate = new Date();
  const sessionDate = createdDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const evaluatedTime = createdDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

  const infoY = 138;
  const col1ValX = PAGE_MARGIN + 55;
  const col1MaxW = 215;
  const col2LabelX = PAGE_MARGIN + 285;
  const col2ValX = PAGE_MARGIN + 330;
  const col2MaxW = 165;

  const addRow = (l1, v1, l2, v2, ry) => {
    // Column 1
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(l1, PAGE_MARGIN, ry);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...WHITE);
    doc.text(truncateToWidth(v1, col1MaxW), col1ValX, ry);

    // Column 2 (safe 15pt+ gap from col 1 max width)
    if (l2) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(l2, col2LabelX, ry);
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...WHITE);
      doc.text(truncateToWidth(v2, col2MaxW), col2ValX, ry);
    }
  };

  addRow('Student:', studentName, 'USN:', usn, infoY);
  addRow('Institution:', institution, 'Date:', sessionDate, infoY + 18);
  addRow('Evaluated:', evaluatedTime, '', '', infoY + 36);

  y = 250;

  // 3. Overall ATS Score & Persona Card
  ensureSpace(80);
  const persona = scorerResult.creative_analysis?.persona;
  const industryFit = scorerResult.creative_analysis?.industry_fit;

  // Score box background
  doc.setFillColor(240, 249, 255); // sky-50
  doc.setDrawColor(186, 230, 253); // sky-200
  doc.setLineWidth(1);
  doc.roundedRect(PAGE_MARGIN, y, contentWidth, 68, 8, 8, 'FD');

  // Score text
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...PRIMARY_BLUE);
  doc.text('OVERALL ATS RESUME SCORE', PAGE_MARGIN + 16, y + 26);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED_COLOR);
  let subMetrics = [];
  if (persona) subMetrics.push(`Persona: ${persona}`);
  if (industryFit) subMetrics.push(`Industry Fit: ${industryFit}%`);
  const subMetricsStr = subMetrics.join('   |   ') || 'Standard ATS Parsing & Relevance Evaluation';
  // Clamp subtitle so it never touches the big score digits on the right
  const maxSubMetricWidth = contentWidth - 110;
  doc.text(truncateToWidth(subMetricsStr, maxSubMetricWidth), PAGE_MARGIN + 16, y + 46);

  // Big score number
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(2, 132, 199); // sky-600
  doc.text(`${scoreVal}/100`, pageWidth - PAGE_MARGIN - 20, y + 42, { align: 'right' });
  y += 86;

  // 4. Analysis Summary
  if (scorerResult.summary) {
    addSectionHeader('Executive Analysis Summary', PRIMARY_BLUE);
    addWrappedText(scorerResult.summary, { fontSize: 9.5, lineHeight: 14, gapAfter: 14 });
  }

  // 5. AI Pro-Tip (if present)
  if (scorerResult.creative_analysis?.pro_tip) {
    const tipText = doc.splitTextToSize(scorerResult.creative_analysis.pro_tip, contentWidth - 32);
    const boxHeight = 24 + tipText.length * 13;
    ensureSpace(boxHeight + 12);
    doc.setFillColor(254, 252, 232); // yellow-50
    doc.setDrawColor(254, 240, 138); // yellow-200
    doc.setLineWidth(1);
    doc.roundedRect(PAGE_MARGIN, y, contentWidth, boxHeight, 6, 6, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(161, 98, 7); // amber-700
    doc.text('AI PRO-TIP & STRATEGY', PAGE_MARGIN + 14, y + 16);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(113, 63, 18); // yellow-900
    let tipY = y + 30;
    tipText.forEach((line) => {
      doc.text(line, PAGE_MARGIN + 14, tipY);
      tipY += 13;
    });
    y += boxHeight + 16;
  }

  // 6. Skill Proficiency Map
  if (Array.isArray(scorerResult.skill_map) && scorerResult.skill_map.length > 0) {
    addSectionHeader('Skill Proficiency Map', PRIMARY_BLUE);
    
    scorerResult.skill_map.forEach((item) => {
      ensureSpace(24);
      const cat = item.category || 'Skill';
      const sScore = typeof item.score === 'number' ? item.score : parseInt(item.score, 10) || 0;

      // Label and score text
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...BODY_COLOR);
      doc.text(truncateToWidth(cat, contentWidth - 70), PAGE_MARGIN + 4, y);
      doc.setFont('helvetica', 'bold');
      doc.text(`${sScore}/10`, pageWidth - PAGE_MARGIN - 4, y, { align: 'right' });
      y += 6;

      // Progress bar track
      doc.setFillColor(241, 245, 249); // slate-100
      doc.roundedRect(PAGE_MARGIN + 4, y, contentWidth - 8, 6, 3, 3, 'F');

      // Progress bar fill
      const fillWidth = Math.max(4, ((contentWidth - 8) * Math.min(sScore, 10)) / 10);
      doc.setFillColor(...PRIMARY_BLUE);
      doc.roundedRect(PAGE_MARGIN + 4, y, fillWidth, 6, 3, 3, 'F');
      y += 16;
    });
    y += 10;
  }

  // 7. Key Strengths
  if (Array.isArray(scorerResult.strengths) && scorerResult.strengths.length > 0) {
    addSectionHeader('Key Resume Strengths', SUCCESS_GREEN);
    scorerResult.strengths.forEach((st) => {
      ensureSpace(20);
      doc.setFillColor(...SUCCESS_GREEN);
      doc.circle(PAGE_MARGIN + 6, y - 3, 2.5, 'F');
      addWrappedText(st, { indent: 16, fontSize: 9.5, lineHeight: 14, gapAfter: 6 });
    });
    y += 10;
  }

  // 8. Areas for Improvement & Recommended Rewrites
  if (Array.isArray(scorerResult.improvements) && scorerResult.improvements.length > 0) {
    addSectionHeader('Areas for Improvement & Action Items', WARNING_AMBER);
    scorerResult.improvements.forEach((imp) => {
      const suggestion = typeof imp === 'string' ? imp : imp.suggestion || '';
      const reason = typeof imp === 'object' && imp?.reason ? imp.reason : null;
      const original = typeof imp === 'object' && imp?.original_sentence ? imp.original_sentence : null;
      const rewrite = typeof imp === 'object' && imp?.rewrite_example ? imp.rewrite_example : null;

      ensureSpace(30);
      doc.setFillColor(...WARNING_AMBER);
      doc.circle(PAGE_MARGIN + 6, y - 3, 2.5, 'F');
      addWrappedText(suggestion, { indent: 16, fontSize: 9.5, fontStyle: 'bold', lineHeight: 14, gapAfter: 3 });

      if (reason) {
        addWrappedText(`Why: ${reason}`, { indent: 16, fontSize: 8.5, color: MUTED_COLOR, gapAfter: 6 });
      }

      if (original || rewrite) {
        if (original) {
          const origLines = doc.splitTextToSize(original, contentWidth - 36);
          const origBoxHeight = 16 + (origLines.length + 1) * 12;
          ensureSpace(origBoxHeight + 8);
          doc.setFillColor(254, 242, 242); // red-50
          doc.setDrawColor(254, 202, 202); // red-200
          doc.roundedRect(PAGE_MARGIN + 16, y, contentWidth - 16, origBoxHeight, 4, 4, 'FD');
          
          let curY = y + 14;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8.5);
          doc.setTextColor(185, 28, 28); // red-700
          doc.text("Original:", PAGE_MARGIN + 24, curY);
          
          curY += 12;
          doc.setFont('helvetica', 'normal');
          origLines.forEach((l) => {
            doc.text(l, PAGE_MARGIN + 24, curY);
            curY += 12;
          });
          y += origBoxHeight + 6;
        }

        if (rewrite) {
          const rewriteLines = doc.splitTextToSize(rewrite, contentWidth - 36);
          const rewriteBoxHeight = 16 + (rewriteLines.length + 1) * 12;
          ensureSpace(rewriteBoxHeight + 8);
          doc.setFillColor(240, 253, 244); // green-50
          doc.setDrawColor(187, 247, 208); // green-200
          doc.roundedRect(PAGE_MARGIN + 16, y, contentWidth - 16, rewriteBoxHeight, 4, 4, 'FD');
          
          let curY = y + 14;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8.5);
          doc.setTextColor(21, 128, 61); // green-700
          doc.text("Suggested Rewrite:", PAGE_MARGIN + 24, curY);
          
          curY += 12;
          doc.setFont('helvetica', 'normal');
          rewriteLines.forEach((l) => {
            doc.text(l, PAGE_MARGIN + 24, curY);
            curY += 12;
          });
          y += rewriteBoxHeight + 8;
        }
      }
      y += 6;
    });
    y += 10;
  }

  // 9. Recommended Career Paths
  if (Array.isArray(scorerResult.career_paths) && scorerResult.career_paths.length > 0) {
    addSectionHeader('Target Career Pathways', PRIMARY_BLUE);
    scorerResult.career_paths.forEach((cp) => {
      ensureSpace(18);
      doc.setFillColor(...PRIMARY_BLUE);
      doc.circle(PAGE_MARGIN + 6, y - 3, 2.5, 'F');
      addWrappedText(cp, { indent: 16, fontSize: 9.5, lineHeight: 14, gapAfter: 4 });
    });
  }

  // Final footer on last page
  addFooter();

  // Save the PDF
  const cleanName = studentName.replace(/[^a-zA-Z0-9_-]/g, '_');
  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`EdVergenceX_Resume_Score_${cleanName}_${dateStr}.pdf`);
};
